import {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {
  Button,
  Input,
  message,
  Modal,
  Row,
  Select,
  Spin,
  Tabs,
  Typography,
  Upload,
  Checkbox,
} from 'antd';

import {CreateAssetBlock} from '../../components';
import {
  Asset,
  AssetType,
  ProgramType,
  QUESTION_TYPE,
  TestTypes,
} from '../../graphql/API';
import type {QuestionWithNewFields} from '../Questions/QuestionTypes';
import {TestsService} from './TestsService';
import {validateCreateTest} from '../../utils/Validators';
import {BackendQuestionType} from '../../types/newQuestionType';
import {CloseCircleFilled, UploadOutlined} from '@ant-design/icons';
import {UploadFile, UploadProps} from 'antd/lib/upload/interface';
import {StorageService, UploadFileType} from '../Storage/StorageService';
import {CreateAssetType} from '../../components/CreateAssetType';
import {CreateAssetUpload} from '../../components/CreateAssetUpload';
import {QUESTION_TYPE_DICT} from './QuestionTypeDict';
import {ProgramsNewService} from '../Program/ProgramsNewService';
import {
  detectBlankKeywords,
  detectChoicesKeywords,
  duplicateBlankKeywordsByWordCount,
  duplicateChoicesKeywordsByWordCount,
  replaceBlankKeywordsInText,
  replaceChoicesInText,
} from './utils/comprehensionParser';
import {
  getQuestionStaticValues,
  getStaticImageUrl,
} from '../../utils/staticValuesUtils/questionStaticValues';
import {getDailyExerciseSummary} from '../../utils/staticValuesUtils/dailyExerciseStaticValue';
import {RearrangeSentenceEditor} from './components/RearrangeSentenceEditor';
const testService = new TestsService();
const storageService = new StorageService();
const programsNewService = new ProgramsNewService();

export const TestCreate = () => {
  const history = useHistory();
  const {Title, Text} = Typography;
  const {TabPane} = Tabs;

  // Get initial static values for default question
  const initialValues = getQuestionStaticValues(
    QUESTION_TYPE.READ_SENTENCE,
    'PHOTO',
  );

  const emptyTestPane = {
    name: '',
    //@ts-ignore
    correctAsset: {
      type: AssetType.AUDIO,
      thumbnailUri: '',
      fileUri: '',
    },
    //@ts-ignore
    asset: {
      type: AssetType.PHOTO,
      thumbnailUri: '',
      fileUri: '',
    },
    programType: ProgramType.LANGUAGE,
    summary: initialValues.summary,
    description: initialValues.description,
    text: '',
    textAI: '',
    originalTextAI: '',
    correctIndex: {},
    choices: [],
    choicesCount: {},
    type: QUESTION_TYPE.READ_SENTENCE,
    program: '', // NEW: Program ID for the question
    difficulty: 'NORMAL', // NEW: Difficulty level (EASY, NORMAL, HARD)
  };

  //@ts-ignore
  const [panes, setPanes] = useState<Array<any>>([emptyTestPane]);
  const [activeKey, setActiveKey] = useState<any>(0);

  const [testName, setTestName] = useState<string>('');
  const [programsList, setProgramsList] = useState<any[]>([]); // NEW: Programs list

  const [fileAudio, setFileAudio] = useState<UploadFile>();

  const [uploading, setUploading] = useState(false);
  const [fileSummaryVideo, setFileSummaryVideo] = useState<UploadFile>();

  const [selectChoice, setSelectChoice] = useState<string>('');

  const [testType, setTestType] = useState<TestTypes | undefined>(
    TestTypes.QUIZ,
  );
  // @ts-ignore
  const [testAsset, setTestAsset] = useState<Asset | undefined>({
    type: AssetType.VIDEO,
    thumbnailUri: '',
    fileUri: '',
  });
  const [testDescription, setTestDescription] = useState<string>(
    'Watch the video, then record your answers.',
  );
  const [testSummary, setTestSummary] = useState<string>(
    'Watch carefully at least 2 times and answer the questions.',
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [questionName, setQuestionName] = useState<string>('');
  const [questionDescription, setQuestionDescription] = useState<string>('');
  const [questionSummary, setQuestionSummary] = useState<string>('');
  const [shownChoicesAlerts, setShownChoicesAlerts] = useState<Set<number>>(
    new Set(),
  );
  useEffect(() => {
    // NEW: Fetch programs list
    programsNewService
      .getNewPrograms()
      .then(programs => {
        console.log('programs_list', programs);
        setProgramsList(programs);

        // Auto-select first program for all panes if they don't have one
        if (programs.length > 0) {
          const firstProgramId = programs[0]._id || programs[0].id;
          setPanes(prevPanes =>
            prevPanes.map(pane => ({
              ...pane,
              program: pane.program || firstProgramId,
            })),
          );
          console.log('Auto-selected first program:', firstProgramId);
        }
      })
      .catch(error => {
        console.error('Error fetching programs:', error);
      });

    return () => {};
  }, []);

  const onChange = (activeKey: string) => {
    setActiveKey(parseInt(activeKey));
    setFileSummaryVideo(null);
  };

  const onEdit = (targetKey: any, action: any) => {
    switch (action) {
      case 'add':
        add();
        break;
      default:
    }
  };

  const add = () => {
    const activeKey = panes.length;
    const newPanes = panes;

    // Create a fresh copy of emptyTestPane to avoid reference issues
    const freshPane = {
      ...emptyTestPane,
      asset: {...emptyTestPane.asset},
      correctAsset: {...emptyTestPane.correctAsset},
    };

    // @ts-ignore
    newPanes.push(freshPane);
    setPanes(newPanes);
    setActiveKey(activeKey);
  };
  const propsAudioUpload: UploadProps = {
    type: 'select',
    accept: '.aac',
    listType: 'text',
    multiple: false,
    maxCount: 1,
    onRemove: file => {
      setFileAudio(null);
    },
    beforeUpload: file => {
      setFileAudio(file);

      return false;
    },
  };

  const propsSummaryVideo: UploadProps = {
    type: 'select',
    accept: '.mp4,.aac',
    listType: 'text',
    multiple: false,
    maxCount: 1,
    onRemove: file => {
      setFileSummaryVideo(null);
    },
    beforeUpload: file => {
      setFileSummaryVideo(file);

      return false;
    },
  };

  const handleUpload = async (
    fileUri: UploadFile,
    typeUpload: UploadFileType,
    index: number,
  ) => {
    setUploading(true);
    try {
      let filePath: string;
      switch (typeUpload) {
        case UploadFileType.TEST_MEDIA:
          filePath = await storageService.uploadFile(
            fileUri,
            typeUpload,
            'test/media',
          );
          let tempAsset = {
            fileUri: filePath,
            type: AssetType.AUDIO,
            thumbnailUri: '',
          };
          let tempPanes = [...panes];
          // @ts-ignore
          tempPanes[index].correctAsset = tempAsset;
          setPanes(tempPanes);
          break;

        case UploadFileType.TEST_MEDIA_VIDEO_SESSION:
          filePath = await storageService.uploadFile(
            fileUri,
            typeUpload,
            'test/media',
          );
          let tempPaness = [...panes];
          // @ts-ignore
          tempPaness[index].videoAISessionUri = filePath;
          setPanes(tempPaness);
          break;

        default:
          break;
      }

      //console.log('file_PATH_IUPLOADED', filePath);
      message.success('upload successfully.');

      setUploading(false);
    } catch (error) {
      setUploading(false);
      message.error('upload failed.');
    }
  };

  const handleTestAssetTypeChange = asset => {
    if (asset.type === 'PHOTO') {
      setTestDescription('Look and record your answer.');
      setTestSummary('Record with your own voice.');
      setTestAsset(asset);
    } else if (asset.type === 'VIDEO') {
      setTestDescription('Watch the video, then record your answers.');
      setTestSummary(
        'Watch carefully at least 2 times and answer the questions.',
      );
      setTestAsset(asset);
    } else if (asset.type === 'AUDIO') {
      setTestDescription('');
      setTestSummary('');
      setTestAsset(asset);
    }
    // //console.log("TestAsset",testAsset,asset.type);
  };
  const handleQuestionTypeChange = (
    type: string,
    asset: string,
    index?: number,
  ) => {
    const {summary, description} = getQuestionStaticValues(type, asset);

    setQuestionSummary(summary);
    setQuestionDescription(description);

    // Update the panes array if index is provided
    if (index !== undefined) {
      let tempPanes = [...panes];
      tempPanes[index].type = type;
      tempPanes[index].summary = summary;
      tempPanes[index].description = description;
      //@ts-ignore
      tempPanes[index].customChoices = [];

      // Set static image URL as default for FREE_TEXT_ONLY_PRONUNCIATION
      // Only set thumbnailUri (photo field), not fileUri (video field)
      const staticImageUrl = getStaticImageUrl(type);
      if (staticImageUrl && !tempPanes[index].asset?.thumbnailUri) {
        tempPanes[index].asset = {
          type: AssetType.PHOTO,
          fileUri: tempPanes[index].asset?.fileUri || '', // Keep existing video URL or empty
          thumbnailUri: staticImageUrl, // Set only the photo field
        } as Asset;
      }

      setPanes(tempPanes);
    }
  };

  function cleanStringForAI(input) {
    return input
      .toLowerCase() // Convert to lowercase
      .replace(/[^a-z0-9'\s]/g, ''); // Remove special characters and punctuation, except apostrophe
  }

  const isKeywordQuestionType = (type: string) =>
    type === QUESTION_TYPE.COMPREHENSION ||
    (type as string) === 'FILL_IN_THE_BLANKS';

  const getKeywordConfig = (type: string) => {
    const isFillInTheBlanks = (type as string) === 'FILL_IN_THE_BLANKS';

    return {
      detectKeywords: isFillInTheBlanks
        ? detectBlankKeywords
        : detectChoicesKeywords,
      duplicateKeywordsByWordCount: isFillInTheBlanks
        ? duplicateBlankKeywordsByWordCount
        : duplicateChoicesKeywordsByWordCount,
      replaceKeywordsInText: isFillInTheBlanks
        ? replaceBlankKeywordsInText
        : replaceChoicesInText,
      keywordExample: isFillInTheBlanks ? 'BLANK1' : 'CHOICES1',
    };
  };

  const buildKeywordBlanks = pane => {
    const customChoices = pane.customChoices || {};
    const correctIndex = pane.correctIndex || {};

    return Object.keys(customChoices).map(choiceKey => {
      const rawChoices = customChoices[choiceKey] || [];
      const isFillInTheBlanks = (pane.type as string) === 'FILL_IN_THE_BLANKS';
      const choices = isFillInTheBlanks ? [rawChoices[0] || ''] : rawChoices;
      const answerIndex = isFillInTheBlanks ? 0 : correctIndex[choiceKey];

      return {
        name: choiceKey,
        choices,
        correctAnswer:
          answerIndex !== undefined ? choices[answerIndex] || '' : '',
      };
    });
  };

  const updateKeywordDerivedFields = (tempPanes: any[], index: number) => {
    const pane = tempPanes[index];
    const keywordConfig = getKeywordConfig(pane.type);
    const originalText = pane.originalTextAI || pane.text || '';
    const blanks = buildKeywordBlanks(pane);

    tempPanes[index].text = keywordConfig.duplicateKeywordsByWordCount(
      originalText,
      blanks,
    );
    tempPanes[index].textAI = keywordConfig.replaceKeywordsInText(
      originalText,
      blanks,
    );
    tempPanes[index].payload = {
      ...(tempPanes[index].payload || {}),
      blanks,
    };
  };

  const syncKeywordChoicesFromText = (index: number) => {
    const keywordConfig = getKeywordConfig(panes[index].type);
    const detectedKeywords = keywordConfig.detectKeywords(
      panes[index].text || '',
      panes[index].textAI || '',
    );

    if (detectedKeywords.length === 0) {
      if (!shownChoicesAlerts.has(index)) {
        alert(
          `Must Text or AI Text Contains at least one keyword ${keywordConfig.keywordExample}`,
        );
        setShownChoicesAlerts(new Set(shownChoicesAlerts).add(index));
      }
    } else if (shownChoicesAlerts.has(index)) {
      const newAlerts = new Set(shownChoicesAlerts);
      newAlerts.delete(index);
      setShownChoicesAlerts(newAlerts);
    }

    const tempPanes = [...panes];
    const previousChoices = tempPanes[index].customChoices || {};
    const previousChoicesCount = tempPanes[index].choicesCount || {};
    const previousCorrectIndex = tempPanes[index].correctIndex || {};
    const customChoices = {};
    const choicesCount = {};
    const correctIndex: Record<string, number> = {};

    detectedKeywords.forEach(keyword => {
      const choices = previousChoices[keyword] || [];

      if ((tempPanes[index].type as string) === 'FILL_IN_THE_BLANKS') {
        customChoices[keyword] = [choices[0] || ''];
        choicesCount[keyword] = 1;
        correctIndex[keyword] = 0;
        return;
      }

      customChoices[keyword] = [...choices];
      choicesCount[keyword] =
        previousChoicesCount[keyword] || Math.max(2, choices.length);

      if (previousCorrectIndex[keyword] !== undefined) {
        correctIndex[keyword] = previousCorrectIndex[keyword];
      }
    });

    tempPanes[index].customChoices = customChoices;
    tempPanes[index].choicesCount = choicesCount;
    tempPanes[index].correctIndex = correctIndex;
    tempPanes[index].payload = {
      ...(tempPanes[index].payload || {}),
      blanks: buildKeywordBlanks(tempPanes[index]),
    };

    if (detectedKeywords.length > 0) {
      setSelectChoice(prevChoice =>
        detectedKeywords.includes(prevChoice)
          ? prevChoice
          : detectedKeywords[0],
      );
    }

    setPanes(tempPanes);
  };
  return (
    <>
      <div style={{padding: 10}}>
        <Title style={{textAlign: 'center'}}>Create test </Title>
        <>
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text>Test name </Text>
            <Input
              onChange={e => {
                setTestName(e.target.value);
                setQuestionName(e.target.value);
                let newPanes = [...panes];
                newPanes[0].name = e.target.value;
                setPanes(newPanes);
              }}
              placeholder="Test name"
            />
          </div>
          {/* <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text style={{display: 'block'}}>Test type </Text>
            <Select
              value={testType}
              showSearch
              style={{width: '100%'}}
              placeholder="Select a test type"
              optionFilterProp="children"
              onChange={(selectedTestType: TestTypes) => {
                setTestType(selectedTestType);
              }}>
              <Select.Option value={TestTypes.QUIZ}>
                {TestTypes.QUIZ}
              </Select.Option>
              <Select.Option value={TestTypes.PLACEMENT_TEST}>
                {TestTypes.PLACEMENT_TEST}
              </Select.Option>
              <Select.Option value={TestTypes.STORY}>
                {TestTypes.STORY}
              </Select.Option>
            </Select>
          </div> */}
          <CreateAssetType
            defaultAssetType={AssetType.VIDEO}
            // @ts-ignore TODO be checked
            onChange={selectedAsset => handleTestAssetTypeChange(selectedAsset)}
          />
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text>Test description </Text>
            <Input
              value={testDescription}
              onChange={e => {
                setTestDescription(e.target.value);
              }}
              placeholder="Test description"
            />
          </div>
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text>Test summary </Text>
            <Input
              value={getDailyExerciseSummary(testAsset.type, panes.length)}
              onChange={e => {
                setTestSummary(e.target.value);
              }}
              disabled
              placeholder="Test summary"
            />
          </div>

          <CreateAssetUpload
            defaultAssetType={testAsset.type}
            asset={testAsset}
            // @ts-ignore TODO be checked
            onChange={selectedAsset => setTestAsset(selectedAsset)}
          />
        </>

        <Tabs
          type="editable-card"
          onChange={onChange}
          activeKey={activeKey.toString()}
          onEdit={onEdit}>
          {panes?.map((pane, index) => (
            <TabPane
              tab={'question_' + index}
              key={index}
              closable={false}
              style={{
                border: '2px solid gray',
                padding: '10px',
              }}>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Question name</Text>
                <Input
                  onChange={e => {
                    let tempPanes = [...panes];
                    tempPanes[index].name = e.target.value;
                    setPanes(tempPanes);
                  }}
                  placeholder="Question name"
                  value={panes[index].name}
                />
              </div>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text style={{display: 'block'}}>Question type </Text>
                <Select
                  value={panes[index].type}
                  showSearch
                  style={{width: 300}}
                  placeholder="Select a question type"
                  optionFilterProp="children"
                  onChange={(selectedQuestionType: any) => {
                    handleQuestionTypeChange(
                      selectedQuestionType,
                      panes[index].asset.type,
                      index,
                    );
                  }}>
                  <Select.Option value={QUESTION_TYPE.READ_SENTENCE}>
                    {QUESTION_TYPE.READ_SENTENCE} (default)
                  </Select.Option>
                  {/*MUST ENTER 4 Choices*/}
                  <Select.Option value={QUESTION_TYPE.CHOICES}>
                    {QUESTION_TYPE.CHOICES}
                  </Select.Option>
                  {/*MUST ENTER TEXTAI*/}
                  <Select.Option value={QUESTION_TYPE.MISSING_LETTERS}>
                    {QUESTION_TYPE.MISSING_LETTERS}
                  </Select.Option>
                  <Select.Option value={QUESTION_TYPE.MISSING_WORD}>
                    {QUESTION_TYPE.MISSING_WORD}
                  </Select.Option>
                  {/*MUST ENTER 4 Choices*/}
                  <Select.Option value={QUESTION_TYPE.COMPREHENSION}>
                    {QUESTION_TYPE.COMPREHENSION}
                  </Select.Option>
                  {/* TRANSCRIBE_AND_SCORE */}
                  <Select.Option value={QUESTION_TYPE.TRANSCRIBE_AND_SCORE}>
                    {QUESTION_TYPE.TRANSCRIBE_AND_SCORE}
                  </Select.Option>
                  {/* DESCRIBE_IMAGE */}
                  <Select.Option value={BackendQuestionType.DESCRIBE_IMAGE}>
                    {BackendQuestionType.DESCRIBE_IMAGE}
                  </Select.Option>
                  {/* FREE_TEXT_ONLY_PRONUNCIATION */}
                  <Select.Option
                    value={QUESTION_TYPE.FREE_TEXT_ONLY_PRONUNCIATION}>
                    {QUESTION_TYPE.FREE_TEXT_ONLY_PRONUNCIATION}
                  </Select.Option>
                  {/* FILL_IN_THE_BLANKS */}
                  <Select.Option value={'FILL_IN_THE_BLANKS' as any}>
                    {'FILL_IN_THE_BLANKS' as any}
                  </Select.Option>
                  {/* REARRANGE_SENTENCE */}
                  <Select.Option value={'REARRANGE_SENTENCE' as any}>
                    {'REARRANGE_SENTENCE' as any}
                  </Select.Option>
                </Select>
              </div>
              {/* NEW: Program Dropdown */}
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Program *</Text>
                <Select
                  style={{width: '100%', marginTop: '5px'}}
                  placeholder="Select a program"
                  value={panes[index].program || undefined}
                  optionFilterProp="children"
                  onChange={(selectedProgramId: string) => {
                    let tempPanes = [...panes];
                    tempPanes[index].program = selectedProgramId;
                    setPanes(tempPanes);
                  }}>
                  {programsList.map((program: any, programIndex) => (
                    <Select.Option
                      value={program.id || program._id}
                      key={programIndex}>
                      {program.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              {/* NEW: Difficulty Selector */}
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Difficulty Level</Text>
                <Select
                  style={{width: '100%', marginTop: '5px'}}
                  placeholder="Select difficulty"
                  value={panes[index].difficulty || 'NORMAL'}
                  optionFilterProp="children"
                  onChange={(
                    selectedDifficulty: 'EASY' | 'NORMAL' | 'HARD',
                  ) => {
                    let tempPanes = [...panes];
                    tempPanes[index].difficulty = selectedDifficulty;
                    setPanes(tempPanes);
                  }}>
                  <Select.Option value="EASY">Easy</Select.Option>
                  <Select.Option value="NORMAL">Normal</Select.Option>
                  <Select.Option value="HARD">Hard</Select.Option>
                </Select>
              </div>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Question Summary</Text>
                <Input
                  value={(() => {
                    const result = getQuestionStaticValues(
                      panes[index].type,
                      testAsset?.type,
                    );
                    return result.summary;
                  })()}
                  onChange={e => {}}
                  placeholder="Question Summary"
                  disabled
                />
              </div>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Question description</Text>
                <Input
                  value={(() => {
                    const result = getQuestionStaticValues(
                      panes[index].type,
                      testAsset?.type,
                    );
                    return result.description;
                  })()}
                  onChange={e => {}}
                  placeholder="Question description"
                  disabled
                />
              </div>
              {testType === TestTypes.STORY && (
                <div style={{marginTop: '20px', marginBottom: '20px'}}>
                  <Text>Video AI Session url </Text>

                  {panes[index].videoAISessionUri ? (
                    <Row
                      style={{
                        display: 'flex',
                        // justifyContent: 'center',
                        alignItems: 'center',
                        alignContent: 'center',
                      }}>
                      <Input
                        style={{
                          width: '80%',
                        }}
                        value={panes[index].videoAISessionUri}
                        onChange={e => {}}
                        placeholder="Basic usage"
                      />
                      {panes[index].videoAISessionUri && (
                        <CloseCircleFilled
                          style={{marginInline: 20}}
                          onClick={() => {
                            let tempPanes = [...panes];
                            tempPanes[index].videoAISessionUri = null;
                            setPanes(tempPanes);
                            setFileSummaryVideo(null);
                          }}
                        />
                      )}
                    </Row>
                  ) : (
                    <Row>
                      <Upload {...propsSummaryVideo}>
                        <Button
                          disabled={fileSummaryVideo ? true : false}
                          icon={<UploadOutlined />}>
                          Select File
                        </Button>
                      </Upload>

                      <Button
                        type="primary"
                        onClick={() => {
                          handleUpload(
                            fileSummaryVideo,
                            UploadFileType.TEST_MEDIA_VIDEO_SESSION,
                            index,
                          );
                        }}
                        disabled={!fileSummaryVideo}
                        loading={uploading}>
                        {uploading ? 'Uploading' : 'Start Upload'}
                      </Button>
                    </Row>
                  )}
                </div>
              )}
              {pane.type === QUESTION_TYPE.CHOICES ? (
                <div>
                  {new Array(4).fill(null).map((_, arrayIndex) => {
                    return (
                      <div
                        key={arrayIndex}
                        style={{marginTop: '20px', marginBottom: '20px'}}>
                        <Text>Choice number {arrayIndex + 1}</Text>
                        <Input
                          value={panes[index].choices[arrayIndex]}
                          onChange={e => {
                            let tempPanes = [...panes];
                            //@ts-ignore
                            tempPanes[index].choices[arrayIndex] =
                              e.target.value;

                            setPanes(tempPanes);
                          }}
                          placeholder={`Choice number ${arrayIndex + 1}`}
                        />
                      </div>
                    );
                  })}
                  <div
                    style={{
                      marginTop: '20px',
                      marginBottom: '20px',
                      border: '2px solid red',
                    }}>
                    <Text>Question text</Text>
                    <Input
                      value={panes[index].text}
                      onChange={e => {
                        let tempPanes = [...panes];
                        tempPanes[index].text = e.target.value;
                        tempPanes[index].textAI = cleanStringForAI(
                          e.target.value,
                        );
                        setPanes(tempPanes);
                      }}
                      placeholder="Question text"
                    />
                  </div>
                </div>
              ) : (pane.type as string) !==
                BackendQuestionType.REARRANGE_SENTENCE ? (
                <div style={{marginTop: '20px', marginBottom: '20px'}}>
                  <Text>Question text</Text>
                  <Input
                    value={panes[index].text}
                    onBlur={() => {
                      if (isKeywordQuestionType(pane.type)) {
                        syncKeywordChoicesFromText(index);
                      } else {
                        let tempPanes = [...panes];

                        //@ts-ignore
                        tempPanes[index].customChoices = {};
                        setPanes(tempPanes);
                      }
                    }}
                    onChange={e => {
                      let tempPanes = [...panes];
                      tempPanes[index].text = e.target.value;

                      if (isKeywordQuestionType(tempPanes[index].type)) {
                        tempPanes[index].textAI = e.target.value;
                        // store the original ai text to able the user change the correct answer
                        //@ts-ignore
                        tempPanes[index].originalTextAI = e.target.value;
                      } else {
                        tempPanes[index].textAI = cleanStringForAI(
                          e.target.value,
                        );
                      }

                      setPanes(tempPanes);
                    }}
                    placeholder="Question text"
                  />
                </div>
              ) : null}
              <div
                style={{
                  marginTop: '20px',
                  marginBottom: '20px',
                  border: '2px solid red',
                }}>
                <Text>
                  Question AI text{' '}
                  {pane.type === QUESTION_TYPE.TRANSCRIBE_AND_SCORE && (
                    <span style={{color: '#999', fontSize: '12px'}}>
                      (optional for reference)
                    </span>
                  )}
                </Text>
                <Input
                  value={panes[index].textAI}
                  placeholder={
                    pane.type === QUESTION_TYPE.TRANSCRIBE_AND_SCORE
                      ? 'Enter reference text for scoring (optional)'
                      : 'Question AI text'
                  }
                  onBlur={() => {
                    if (isKeywordQuestionType(pane.type)) {
                      syncKeywordChoicesFromText(index);
                    } else {
                      let tempPanes = [...panes];

                      //@ts-ignore
                      tempPanes[index].customChoices = {};
                      setPanes(tempPanes);
                    }
                  }}
                  onChange={e => {
                    let tempPanes = [...panes];

                    if (
                      isKeywordQuestionType(tempPanes[index].type) ||
                      tempPanes[index].type ===
                        QUESTION_TYPE.TRANSCRIBE_AND_SCORE
                    ) {
                      tempPanes[index].textAI = e.target.value;
                    } else {
                      tempPanes[index].textAI = cleanStringForAI(
                        e.target.value,
                      );
                    }

                    setPanes(tempPanes);
                  }}
                />
              </div>
              {(pane.type as string) === 'REARRANGE_SENTENCE' && (
                <RearrangeSentenceEditor
                  question={panes[index]}
                  onAITextChange={newText => {
                    let tempPanes = [...panes];
                    tempPanes[index].textAI = newText;
                    setPanes(tempPanes);
                  }}
                  onShuffledOrderChange={shuffledWords => {
                    const correctWords =
                      panes[index].textAI
                        ?.trim()
                        .split(/\s+/)
                        .filter((word: string) => word.length > 0) || [];

                    let tempPanes = [...panes];
                    tempPanes[index].payload = {
                      ...tempPanes[index].payload,
                      words: correctWords,
                      shuffledOrder: shuffledWords,
                      correctOrder: correctWords.map(
                        (_: string, wordIndex: number) => wordIndex,
                      ),
                    };
                    setPanes(tempPanes);
                  }}
                />
              )}
              {isKeywordQuestionType(pane.type) && (
                <div>
                  <div>
                    <Text style={{display: 'block'}}>Choice Options </Text>

                    <Select
                      value={selectChoice}
                      showSearch
                      style={{width: '30%'}}
                      placeholder={'Select ' + selectChoice}
                      optionFilterProp="children"
                      onChange={(selectedChoice: string) => {
                        setSelectChoice(selectedChoice);
                        let tempPanes = [...panes];
                        setPanes(tempPanes);
                      }}>
                      {Object.keys(panes[index].customChoices || {}).map(
                        choice => (
                          <Select.Option key={choice} value={choice}>
                            {choice}
                          </Select.Option>
                        ),
                      )}
                    </Select>
                  </div>
                  {selectChoice &&
                    ((pane.type as string) === 'FILL_IN_THE_BLANKS' ? (
                      <div style={{marginTop: '20px', marginBottom: '20px'}}>
                        <Text>Correct answer for {selectChoice}</Text>
                        <Input
                          value={
                            panes[index]?.customChoices?.[selectChoice]?.[0] ||
                            ''
                          }
                          onChange={e => {
                            let tempPanes = [...panes];

                            if (!tempPanes[index].customChoices) {
                              tempPanes[index].customChoices = {};
                            }
                            if (!tempPanes[index].correctIndex) {
                              tempPanes[index].correctIndex = {};
                            }
                            if (!tempPanes[index].choicesCount) {
                              tempPanes[index].choicesCount = {};
                            }

                            tempPanes[index].customChoices[selectChoice] = [
                              e.target.value,
                            ];
                            tempPanes[index].choicesCount[selectChoice] = 1;
                            tempPanes[index].correctIndex[selectChoice] = 0;
                            updateKeywordDerivedFields(tempPanes, index);
                            setPanes(tempPanes);
                          }}
                          placeholder={`Correct answer for ${selectChoice}`}
                        />
                      </div>
                    ) : (
                      <div>
                        {new Array(
                          panes[index]?.choicesCount?.[selectChoice] || 2,
                        )
                          .fill(null)
                          .map((_, arrayIndex) => {
                            const pane = panes[index];
                            const customChoices =
                              pane?.customChoices?.[selectChoice] || [];
                            return (
                              <div
                                key={arrayIndex}
                                style={{
                                  marginTop: '20px',
                                  marginBottom: '20px',
                                }}>
                                <Row align="middle">
                                  <Text>
                                    Choice number {arrayIndex + 1}
                                    <Checkbox
                                      style={{marginLeft: '10px'}}
                                      checked={
                                        pane?.correctIndex?.[selectChoice] ===
                                        arrayIndex
                                      }
                                      onChange={() => {
                                        if (!pane) return;
                                        let tempPanes = [...panes];

                                        if (!tempPanes[index].correctIndex) {
                                          tempPanes[index].correctIndex = {};
                                        }

                                        tempPanes[index].correctIndex[
                                          selectChoice
                                        ] = arrayIndex;
                                        updateKeywordDerivedFields(
                                          tempPanes,
                                          index,
                                        );

                                        setPanes(tempPanes);
                                      }}
                                    />{' '}
                                    Correct answer
                                  </Text>
                                </Row>
                                <Input
                                  value={customChoices[arrayIndex] || ''}
                                  onChange={e => {
                                    if (!pane) return;
                                    let tempPanes = [...panes];
                                    const currentChoices =
                                      tempPanes[index].customChoices[
                                        selectChoice
                                      ] || [];
                                    currentChoices[arrayIndex] = e.target.value;

                                    tempPanes[index].customChoices[
                                      selectChoice
                                    ] = currentChoices;
                                    if (
                                      tempPanes[index].correctIndex?.[
                                        selectChoice
                                      ] === arrayIndex
                                    ) {
                                      updateKeywordDerivedFields(
                                        tempPanes,
                                        index,
                                      );
                                    }
                                    setPanes(tempPanes);
                                  }}
                                  placeholder={`Choice number ${arrayIndex + 1}`}
                                />
                              </div>
                            );
                          })}
                        <Button
                          type="dashed"
                          onClick={() => {
                            let tempPanes = [...panes];

                            let currentCount =
                              tempPanes[index].choicesCount[selectChoice] || 2;

                            if (currentCount < 4) {
                              tempPanes[index].choicesCount[selectChoice] =
                                currentCount + 1;
                              setPanes(tempPanes);
                            }
                          }}
                          disabled={
                            panes[index].choicesCount[selectChoice] >= 4
                          }
                          style={{marginTop: '10px'}}>
                          Add Choice
                        </Button>
                      </div>
                    ))}
                </div>
              )}{' '}
              <div>
                <Text style={{fontSize: '20', fontWeight: 'bold'}}>
                  Question asset
                </Text>
                <CreateAssetBlock
                  defaultAssetType={AssetType.PHOTO}
                  key={`asset-block-${index}`}
                  asset={panes[index].asset}
                  // @ts-ignore TODO be checked
                  onChange={asset => {
                    // Update summary and description when asset type changes
                    const staticValues = getQuestionStaticValues(
                      panes[index].type,
                      asset.type || 'default',
                    );

                    // Create a new array with the updated pane object (deep copy of the specific pane)
                    const tempPanes = [...panes];
                    tempPanes[index] = {
                      ...panes[index],
                      asset: asset,
                      summary: staticValues.summary,
                      description: staticValues.description,
                    };

                    setPanes(tempPanes);
                  }}
                />
              </div>
              {QUESTION_TYPE_DICT[pane.type] == 'NORMAL_SCORE' && (
                <>
                  <div>
                    <Text style={{fontSize: '20', fontWeight: 'bold'}}>
                      Question correct answer asset file url
                    </Text>

                    {panes[index]?.correctAsset?.fileUri ? (
                      <Row
                        style={{
                          display: 'flex',
                          // justifyContent: 'center',
                          alignItems: 'center',
                          alignContent: 'center',
                        }}>
                        <Input
                          style={{width: '80%'}}
                          value={panes[index]?.correctAsset?.fileUri}
                          onChange={e => {}}
                          placeholder="Correct answer url"
                        />

                        <CloseCircleFilled
                          style={{marginInline: 20}}
                          onClick={() => {
                            setFileAudio(null);

                            let tempAsset = {
                              fileUri: '',
                              type: AssetType.AUDIO,
                              thumbnailUri: '',
                            };
                            let tempPanes = [...panes];
                            // @ts-ignore
                            tempPanes[index].correctAsset = tempAsset;
                            setPanes(tempPanes);
                          }}
                        />
                      </Row>
                    ) : (
                      <Row>
                        <Upload {...propsAudioUpload}>
                          <Button
                            disabled={fileAudio ? true : false}
                            icon={<UploadOutlined />}>
                            Select File
                          </Button>
                        </Upload>

                        <Button
                          type="primary"
                          onClick={() =>
                            handleUpload(
                              fileAudio,
                              UploadFileType.TEST_MEDIA,
                              index,
                            )
                          }
                          disabled={!fileAudio}
                          loading={uploading}>
                          {uploading ? 'Uploading' : 'Upload'}
                        </Button>
                      </Row>
                    )}
                  </div>
                </>
              )}
            </TabPane>
          ))}
        </Tabs>

        <Button
          onClick={async () => {
            // setIsLoading(true);
            const questions = panes.map(pane => {
              const staticValues = getQuestionStaticValues(
                pane.type,
                pane.asset?.type || testAsset?.type,
              );

              return {
                ...pane,
                summary: staticValues.summary,
                description: staticValues.description,
              };
            });

            let test = {
              questions,
              type: testType,
              name: testName,
              description: testDescription,
              summary: testSummary,
              asset: testAsset,
            };

            // @ts-ignore
            let testValidationResult = validateCreateTest(test);
            if (testValidationResult?.status) {
              try {
                const response = await testService.createTest(test);
                alert('Test with ID: ' + response?.id + ' created');
                history.push('/tests');
              } catch (e) {
                setIsLoading(false);
                alert('Error creating test');
              }
            } else {
              alert(testValidationResult.message);
              setIsLoading(false);
            }
          }}>
          Create
        </Button>
      </div>
      <Modal
        okButtonProps={{hidden: true}}
        cancelButtonProps={{hidden: true}}
        visible={isLoading}
        closable={true}>
        <div>
          <Spin size="large" style={{margin: 'auto', width: '100%'}} />
          <p style={{textAlign: 'center'}}>Loading...</p>
        </div>
      </Modal>
    </>
  );
};
